import type { AcousticCalibrationSnapshot, AcousticCalibrationTag } from './calibrationTypes';
import { ACOUSTIC_CALIBRATION_TAGS } from './calibrationTypes';
import { clamp01, mean } from './utils';

export type ContinuumMetricKey =
  | 'fusion_loudness'
  | 'fusion_noise'
  | 'fusion_turnTaking'
  | 'fusion_diffuseness'
  | 'fusion_coherence'
  | 'fusion_peaks'
  | 'fusion_crest'
  | 'yamnet_speech'
  | 'yamnet_babble'
  | 'yamnet_shouting';

export const CONTINUUM_METRICS: Array<{ key: ContinuumMetricKey; label: string }> = [
  { key: 'fusion_loudness', label: 'Lautstärke' },
  { key: 'fusion_noise', label: 'Rauschen' },
  { key: 'fusion_turnTaking', label: 'Wechsel' },
  { key: 'fusion_diffuseness', label: 'Diffusheit' },
  { key: 'fusion_coherence', label: 'Kohärenz' },
  { key: 'fusion_peaks', label: 'Peaks' },
  { key: 'fusion_crest', label: 'Crest' },
  { key: 'yamnet_babble', label: 'YAMNet Gemurmel' },
  { key: 'yamnet_shouting', label: 'YAMNet Geschrei' }
];

export type MetricContinuumStats = {
  min: number;
  max: number;
  mean: number;
  count: number;
};

export type CategoryContinuum = {
  tag: AcousticCalibrationTag;
  tagLabel: string;
  snapshotCount: number;
  metrics: Partial<Record<ContinuumMetricKey, MetricContinuumStats>>;
};

const MARGIN_BELOW_MIN = 0.04;
const MARGIN_ABOVE_MAX = 0.05;
const GAP_BETWEEN_CATEGORIES = 0.02;

function readMetric(snap: AcousticCalibrationSnapshot, key: ContinuumMetricKey): number {
  const radar = snap.decisionRadar;
  if (key === 'fusion_peaks') return (radar.fusion_peaks ?? 0) / 10;
  if (key === 'fusion_crest') return (radar.fusion_crest ?? 0) * 6;
  return radar[key] ?? 0;
}

export function statsFromValues(values: number[]): MetricContinuumStats | null {
  if (!values.length) return null;
  return {
    min: Math.min(...values),
    max: Math.max(...values),
    mean: mean(values),
    count: values.length
  };
}

export function buildCategoryContinua(snapshots: AcousticCalibrationSnapshot[]): CategoryContinuum[] {
  const tags = ACOUSTIC_CALIBRATION_TAGS.map((t) => t.key);
  return tags
    .map((tag) => {
      const tagged = snapshots.filter((s) => s.tag === tag);
      if (!tagged.length) return null;
      const metrics: CategoryContinuum['metrics'] = {};
      for (const { key } of CONTINUUM_METRICS) {
        const values = tagged.map((s) => readMetric(s, key)).filter((v) => Number.isFinite(v));
        const stats = statsFromValues(values);
        if (stats) metrics[key] = stats;
      }
      const tagMeta = ACOUSTIC_CALIBRATION_TAGS.find((t) => t.key === tag);
      return {
        tag,
        tagLabel: tagMeta?.label ?? tag,
        snapshotCount: tagged.length,
        metrics
      };
    })
    .filter((entry): entry is CategoryContinuum => entry !== null);
}

/** Untere Schwelle: Kategorie-Kontinuum abdecken (min − Marge), von anderen trennen (deren max + Lücke). */
export function continuumLowerThreshold(
  target: MetricContinuumStats,
  others: MetricContinuumStats[]
): number {
  const coverTarget = clamp01(target.min * (1 - MARGIN_BELOW_MIN));
  if (!others.length) return coverTarget;
  const barrier = clamp01(Math.max(...others.map((o) => o.max)) + GAP_BETWEEN_CATEGORIES);
  return clamp01(Math.max(coverTarget, barrier));
}

/** Obere Schwelle: Kategorie-Kontinuum abdecken (max + Marge), optional unter anderes Kontinuum drücken. */
export function continuumUpperThreshold(
  target: MetricContinuumStats,
  others: MetricContinuumStats[],
  capBelowOthers = true
): number {
  const coverTarget = clamp01(target.max * (1 + MARGIN_ABOVE_MAX));
  if (!capBelowOthers || !others.length) return Math.min(1, coverTarget);
  const cap = Math.min(...others.map((o) => o.min)) - GAP_BETWEEN_CATEGORIES;
  if (cap <= target.min) return Math.min(1, coverTarget);
  return clamp01(Math.min(coverTarget, cap));
}

/** Für „mindestens“-Schwellen mit Schwerpunkt: zwischen min und mean gewichten. */
export function continuumCentroidLowerThreshold(
  target: MetricContinuumStats,
  others: MetricContinuumStats[],
  centroidWeight = 0.35
): number {
  const anchor = target.min * (1 - centroidWeight) + target.mean * centroidWeight;
  const coverTarget = clamp01(anchor * (1 - MARGIN_BELOW_MIN));
  if (!others.length) return coverTarget;
  const barrier = clamp01(Math.max(...others.map((o) => o.max)) + GAP_BETWEEN_CATEGORIES);
  return clamp01(Math.max(coverTarget, barrier));
}

export function getContinuumForTag(
  continua: CategoryContinuum[],
  tag: AcousticCalibrationTag
): CategoryContinuum | undefined {
  return continua.find((c) => c.tag === tag);
}

export function getOtherContinua(continua: CategoryContinuum[], tag: AcousticCalibrationTag): CategoryContinuum[] {
  return continua.filter((c) => c.tag !== tag);
}

export function metricStats(
  continuum: CategoryContinuum | undefined,
  key: ContinuumMetricKey
): MetricContinuumStats | null {
  return continuum?.metrics[key] ?? null;
}

export function otherMetricStats(
  others: CategoryContinuum[],
  key: ContinuumMetricKey
): MetricContinuumStats[] {
  return others.map((c) => c.metrics[key]).filter((s): s is MetricContinuumStats => Boolean(s));
}
